import { MAX_RELIABLE_FRAME } from "./constants";

/** WebSocket channel prefixes (docs/NETCODE.md "Transports"). */
export const WS_UNRELIABLE = 0x00;
export const WS_RELIABLE = 0x01;

/** `[channel][payload]` as one binary WebSocket frame. */
export function encodeWsFrame(channel: number, payload: Uint8Array): Uint8Array<ArrayBuffer> {
  const frame = new Uint8Array(payload.length + 1);
  frame[0] = channel;
  frame.set(payload, 1);
  return frame;
}

/** `[u32 LE length][payload]`, the reliable framing of the WebTransport bidirectional stream. */
export function encodeLengthFrame(payload: Uint8Array): Uint8Array<ArrayBuffer> {
  const frame = new Uint8Array(payload.length + 4);
  new DataView(frame.buffer).setUint32(0, payload.length, true);
  frame.set(payload, 4);
  return frame;
}

export class FrameError extends Error {}

/**
 * Splits a byte stream of `[u32 LE length][payload]` frames into payloads. Chunks may end
 * anywhere, even inside a header; one chunk may hold many frames. Empty frames are skipped.
 * Throws `FrameError` for a declared length above `maxFrame` (the stream is not recoverable).
 */
export class LengthFramer {
  private pending: Uint8Array = new Uint8Array(0);

  constructor(private readonly maxFrame: number = MAX_RELIABLE_FRAME) {}

  push(chunk: Uint8Array, onFrame: (payload: Uint8Array) => void): void {
    let data = chunk;
    if (this.pending.length > 0) {
      data = new Uint8Array(this.pending.length + chunk.length);
      data.set(this.pending, 0);
      data.set(chunk, this.pending.length);
      this.pending = new Uint8Array(0);
    }
    const view = new DataView(data.buffer, data.byteOffset, data.byteLength);
    let off = 0;
    while (data.length - off >= 4) {
      const len = view.getUint32(off, true);
      if (len > this.maxFrame) throw new FrameError(`reliable frame of ${len} bytes exceeds the limit`);
      if (data.length - off - 4 < len) break;
      if (len > 0) onFrame(data.subarray(off + 4, off + 4 + len));
      off += 4 + len;
    }
    if (off < data.length) this.pending = data.slice(off);
  }

  /** Bytes of an incomplete frame waiting for more data. */
  get buffered(): number {
    return this.pending.length;
  }
}
